import { AnimatePresence, m } from "motion/react";
import { type ReactNode, useEffect, useState } from "react";

export interface NoticeItem {
	id?: string | number;
	content: ReactNode;
	type?: number; // 1 for special styling
}

interface NoticeBarProps {
	text?: string; // 单条文本，用于水平跑马灯（向下兼容）
	data?: (string | NoticeItem)[]; // 多条文本或对象，用于垂直轮播
	speed?: number; // 水平滚动速度
	interval?: number; // 垂直轮播间隔时间（毫秒）
	className?: string;
	icon?: React.ReactNode;
}

export default function NoticeBar({
	text,
	data = [],
	speed = 20,
	interval = 3000,
	className = "",
	icon,
}: NoticeBarProps) {
	const [index, setIndex] = useState(0);

	// 如果传入了 data 且长度大于 0，使用垂直轮播模式
	const isVertical = data.length > 0;
	const currentItem = isVertical ? data[index] : text;

	// 解析当前项内容和样式
	let content: ReactNode = "";
	let isSpecial = false;

	if (isVertical) {
		if (typeof currentItem === "object" && currentItem !== null && "content" in currentItem) {
			content = currentItem.content;
			isSpecial = currentItem.type === 1;
		} else {
			content = currentItem as string;
		}
	} else {
		content = text;
	}

	useEffect(() => {
		if (isVertical && data.length > 1) {
			const timer = setInterval(() => {
				setIndex((prev) => (prev + 1) % data.length);
			}, interval);
			return () => clearInterval(timer);
		}
	}, [isVertical, data.length, interval]);

	return (
		<div className={`w-full overflow-hidden bg-[#FFFBE6] text-[#E6A23C] h-[40px] flex items-center ${className}`}>
			<div className="flex-1 overflow-hidden flex items-center h-full relative">
				{isVertical ? (
					<div className="relative h-full w-full">
						<AnimatePresence mode="popLayout">
							<m.div
								key={index}
								className={`absolute inset-0 flex items-center px-4 text-sm font-medium truncate w-full ${
									isSpecial ? "text-[#FF4D4F] font-bold" : ""
								}`}
								initial={{ y: "100%" }}
								animate={{ y: "0%" }}
								exit={{ y: "-100%" }}
								transition={{ duration: 0.5, ease: "easeInOut" }}
							>
								{content}
							</m.div>
						</AnimatePresence>
					</div>
				) : (
					// 原有的水平跑马灯模式
					<m.div
						className="flex whitespace-nowrap"
						animate={{ x: ["0%", "-50%"] }}
						transition={{
							repeat: Number.POSITIVE_INFINITY,
							ease: "linear",
							duration: speed,
						}}
					>
						{[...Array(10)].map((_, i) => (
							<span key={`marquee-${i}`} className="px-8 text-sm font-medium">
								{text}
							</span>
						))}
					</m.div>
				)}
			</div>
		</div>
	);
}
